import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronDown, LogOut } from 'lucide-react';
import { buildMenuItems } from '../pages/dashboard/menuConfig';

/**
 * Global SlideMenu — available on ALL pages via ProtectedRoute.
 * Listens for 'pm-open-menu' custom event dispatched by GlobalHeader.
 */
export default function GlobalSlideMenu({ currentUser, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    const handler = () => setShowMenu(true);
    window.addEventListener('pm-open-menu', handler);
    return () => window.removeEventListener('pm-open-menu', handler);
  }, []);

  // Close menu on route change (most reliable)
  useEffect(() => {
    setShowMenu(false);
  }, [location.pathname]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setShowMenu(false); };
    if (showMenu) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showMenu]);

  if (!showMenu) return null;

  const menuItems = buildMenuItems(currentUser, null, null);
  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={() => setShowMenu(false)} />
      <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85%] bg-white dark:bg-slate-900 shadow-2xl animate-in slide-in-from-right flex flex-col" style={{height: '100%'}}>
        {/* Header */}
        <div className="p-4 bg-[#004C98] text-white" style={{flexShrink: 0}}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold">Μενού</h2>
            <Button onClick={() => setShowMenu(false)} variant="ghost" className="text-white hover:bg-white/10 h-8 w-8 p-0">
              <X size={20} />
            </Button>
          </div>
          {currentUser && (
            <div className="text-blue-100">
              <p className="font-semibold text-sm">{currentUser.name || currentUser.email}</p>
              <p className="text-xs opacity-80">
                {currentUser.role === 'admin' ? 'Διαχειριστής' : 'Χρήστης'}
              </p>
            </div>
          )}
        </div>

        {/* Menu Items */}
        <div className="flex-1 overflow-y-auto p-3 space-y-1" style={{overflowY: 'auto'}}>
          {menuItems.map((group) => {
            if (group.adminOnly && !isAdmin) return null;
            const isExpanded = expandedGroups[group.group];
            const GroupIcon = group.icon;

            return (
              <div key={group.group || group.path}>
                {group.children ? (
                  <>
                    <button
                      onClick={() => setExpandedGroups(prev => ({...prev, [group.group]: !prev[group.group]}))}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <GroupIcon size={18} className={group.color} />
                        <span className="font-semibold text-sm text-slate-700 dark:text-slate-200">{group.label}</span>
                      </div>
                      {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                    </button>
                    {isExpanded && (
                      <div className="ml-4 space-y-0.5 mb-2">
                        {group.children.map((item) => {
                          if (item.adminOnly && !isAdmin) return null;
                          const ItemIcon = item.icon;
                          return (
                        <button
                          key={item.path || item.action}
                          onClick={() => {
                            if (item.path) navigate(item.path);
                            if (item.action) navigate(`/dashboard?modal=${item.action}`);
                            setShowMenu(false);
                          }}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors text-left"
                          data-testid={`menu-item-${(item.path || item.action || '').replace('/', '')}`}
                        >
                          <ItemIcon size={16} className={item.color} />
                          <span className="text-sm text-slate-600 dark:text-slate-300">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                  </>
                ) : (
                  <button
                    onClick={() => {
                      if (group.path) navigate(group.path);
                      if (group.action) navigate(`/dashboard?modal=${group.action}`);
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    data-testid={`menu-item-${(group.path || '').replace('/', '')}`}
                  >
                    <GroupIcon size={18} className={group.color} />
                    <span className="font-semibold text-sm text-slate-700 dark:text-slate-200">{group.label}</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Logout Footer */}
        <div className="p-3 border-t dark:border-slate-700" style={{flexShrink: 0}}>
          <Button onClick={() => { setShowMenu(false); onLogout?.(); }} variant="outline" className="w-full h-10 text-red-600 border-red-200 hover:bg-red-50">
            <LogOut size={16} className="mr-2" /> Αποσύνδεση
          </Button>
        </div>
      </div>
    </div>
  );
}
